(() => {
  const SELECTOR = '.product-card-sizes';
  const SQUARE_VISIBLE = 6;
  const SQUARE_VISIBLE_MOBILE = 4;
  const FIT_VISIBLE_MAX = 2;
  const SIZE_LIKE =
    /^(?:\d+(?:\.\d+)?|XXS|XS|S|M|L|XL|XXL|XXXL|[2-9]X?L|NB|NEWBORN|OS|ONESIZE|\d+\s*-\s*\d+\s*M?|\d+T)$/i;

  function chipLabel(chip) {
    return (chip.getAttribute('title') || chip.textContent || '').trim().replace(/\s+/g, ' ');
  }

  function isSizeLikeLabel(text) {
    if (!text) return false;
    const compact = text.replace(/\s+/g, '');
    if (SIZE_LIKE.test(compact)) return true;
    if (/^\d+\s*-\s*\d+/i.test(text) && text.length <= 12) return true;
    return false;
  }

  /** True only for wordy name chips (Pink Gingham…) — not sizes/age ranges */
  function isLargeNameOnlyRow(el) {
    const chips = Array.from(el.querySelectorAll('.product-card-size'));
    if (!chips.length) return false;
    return chips.every((chip) => {
      if (chip.classList.contains('product-card-size--swatch')) return false;
      const label = chipLabel(chip);
      if (isSizeLikeLabel(label)) return false;
      return (
        chip.classList.contains('product-card-size--fit') ||
        label.includes(' ') ||
        label.length > 6
      );
    });
  }

  function isCombinedRow(el) {
    return el.classList.contains('product-card-sizes--combined');
  }

  function isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function squareVisible() {
    return isMobile() ? SQUARE_VISIBLE_MOBILE : SQUARE_VISIBLE;
  }

  function chipGap(el) {
    const styles = window.getComputedStyle(el);
    return parseFloat(styles.columnGap || styles.gap || '0') || 0;
  }

  function shouldEnhance(el) {
    if (!el || el.closest('.product-card-size-scroller')) return false;
    if (el.querySelector('.product-card-size--unavailable')) return false;
    if (el.querySelector('.product-card-size--one-size') && el.children.length <= 1) return false;

    const count = el.children.length;
    if (count <= 1) return false;

    if (isLargeNameOnlyRow(el)) return count > 1;
    if (isMobile()) return count >= 3;
    return count > SQUARE_VISIBLE;
  }

  function updateButtons(scroller, track, prev, next) {
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    const left = track.scrollLeft;
    const canScroll = max > 1;
    scroller.classList.toggle('is-scrollable', canScroll);
    prev.disabled = !canScroll || left <= 1;
    next.disabled = !canScroll || left >= max - 1;
  }

  function isPriceLikeLabel(text) {
    return /^\$/.test(text || '');
  }

  /** Word names: 1–2 chips. Gift-card prices: same window as square cards (up to 4 / 6). */
  function sizeFitTrack(scroller, track, el) {
    const chips = Array.from(el.querySelectorAll('.product-card-size'));
    if (!chips.length) return;

    const gap = chipGap(el);
    const buttons = scroller.querySelectorAll('.product-card-size-scroller__btn');
    let reserved = 12;
    buttons.forEach((btn) => {
      reserved += btn.getBoundingClientRect().width || 30;
    });

    const available = Math.max(scroller.clientWidth - reserved, chips[0].getBoundingClientRect().width);
    const priceRow = chips.every((chip) => isPriceLikeLabel(chipLabel(chip)));
    const maxNames = priceRow ? squareVisible() : FIT_VISIBLE_MAX;

    let target = 0;
    let count = 0;
    for (const chip of chips.slice(0, maxNames)) {
      const w = chip.getBoundingClientRect().width || chip.offsetWidth;
      const next = count === 0 ? w : target + gap + w;
      if (count > 0 && next > available + 1) break;
      target = next;
      count += 1;
    }
    if (!target) target = chips[0].getBoundingClientRect().width || 80;

    track.style.width = `${Math.min(target, available)}px`;
    track.style.maxWidth = '100%';
  }

  /** Sizes / age ranges / mixed: 6 on desktop; on mobile 2–4 depending on card width */
  function sizeSquareTrack(scroller, track, el) {
    const chips = Array.from(el.querySelectorAll('.product-card-size'));
    if (!chips.length) return;
    const gap = chipGap(el);
    let maxChip = 0;
    chips.slice(0, SQUARE_VISIBLE).forEach((chip) => {
      maxChip = Math.max(maxChip, chip.getBoundingClientRect().width);
    });
    if (!maxChip) maxChip = chips[0].getBoundingClientRect().width;

    if (!isMobile()) {
      const width = maxChip * SQUARE_VISIBLE + gap * (SQUARE_VISIBLE - 1);
      track.style.width = `${width}px`;
      track.style.maxWidth = '100%';
      return;
    }

    const buttons = scroller.querySelectorAll('.product-card-size-scroller__btn');
    let reserved = 12;
    buttons.forEach((btn) => {
      reserved += btn.getBoundingClientRect().width || 30;
    });
    const available = Math.max(scroller.clientWidth - reserved, maxChip);
    const fitCount = Math.max(
      1,
      Math.min(SQUARE_VISIBLE_MOBILE, Math.floor((available + gap) / (maxChip + gap)))
    );
    const width = maxChip * fitCount + gap * Math.max(0, fitCount - 1);
    track.style.width = `${Math.min(width, available)}px`;
    track.style.maxWidth = '100%';
  }

  function pageTarget(track, direction) {
    const chips = Array.from(track.querySelectorAll('.product-card-size'));
    if (!chips.length) return track.scrollLeft;

    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    const left = track.scrollLeft;
    const page = Math.max(track.clientWidth, 1);
    const epsilon = 2;

    if (direction > 0) {
      const ideal = left + page;
      let target = max;
      for (const chip of chips) {
        if (chip.offsetLeft >= ideal - epsilon) {
          target = chip.offsetLeft;
          break;
        }
      }
      if (target === max) {
        for (let i = chips.length - 1; i >= 0; i -= 1) {
          if (chips[i].offsetLeft <= max + epsilon) {
            target = chips[i].offsetLeft;
            break;
          }
        }
      }
      return Math.min(Math.max(target, left + 1), max);
    }

    const ideal = left - page;
    let target = 0;
    for (let i = chips.length - 1; i >= 0; i -= 1) {
      if (chips[i].offsetLeft <= ideal + epsilon) {
        target = chips[i].offsetLeft;
        break;
      }
    }
    return Math.max(0, Math.min(target, left - 1));
  }

  function wrap(el) {
    if (el.closest('.product-card-size-scroller')) return;

    const combined = isCombinedRow(el);
    const largeNames = isLargeNameOnlyRow(el);
    const modeClass = largeNames
      ? 'product-card-size-scroller--fit'
      : 'product-card-size-scroller--square';

    const scroller = document.createElement('div');
    // Start scrollable so arrow buttons reserve space (original behavior)
    scroller.className = `product-card-size-scroller ${modeClass}${combined ? ' product-card-size-scroller--combined' : ''} is-scrollable`;

    const track = document.createElement('div');
    track.className = 'product-card-size-scroller__track';

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'product-card-size-scroller__btn product-card-size-scroller__btn--prev';
    prev.setAttribute('aria-label', 'Previous options');
    prev.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'product-card-size-scroller__btn product-card-size-scroller__btn--next';
    next.setAttribute('aria-label', 'Next options');
    next.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>';

    el.parentNode.insertBefore(scroller, el);
    track.appendChild(el);
    scroller.appendChild(prev);
    scroller.appendChild(track);
    scroller.appendChild(next);

    const layout = () => {
      if (largeNames) sizeFitTrack(scroller, track, el);
      else sizeSquareTrack(scroller, track, el);
      updateButtons(scroller, track, prev, next);
    };

    const go = (direction) => {
      track.scrollTo({ left: pageTarget(track, direction), behavior: 'smooth' });
    };

    prev.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      go(-1);
    });
    next.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      go(1);
    });
    track.addEventListener('scroll', () => updateButtons(scroller, track, prev, next), { passive: true });
    window.addEventListener('resize', layout);

    requestAnimationFrame(layout);
    setTimeout(layout, 50);
    setTimeout(layout, 250);
    setTimeout(layout, 600);
  }

  function enhance(root = document) {
    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (shouldEnhance(el)) wrap(el);
    });
  }

  const run = () => enhance();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', run);
  document.addEventListener('shopify:section:load', (e) => enhance(e.target));

  // Preview / rotate / resize: wrap cards that only need arrows on mobile
  window.addEventListener('resize', run);
  const mobileMq = window.matchMedia('(max-width: 749px)');
  if (typeof mobileMq.addEventListener === 'function') {
    mobileMq.addEventListener('change', run);
  } else if (typeof mobileMq.addListener === 'function') {
    mobileMq.addListener(run);
  }
})();
